import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { exhaustiveSwitchWithDefault } from "@faire/web/common/exhaustiveSwitchWithDefault";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import {
  IOptimizeImageOptions,
  optimizeImageUrl,
  optimizeImageUrlFillRectangle,
  optimizeImageUrlFillSquare,
  optimizeImageUrlHeight,
  optimizeImageUrlWidth,
  optimizeProductImageUrl,
  optimizeProductImageUrlFillVertical,
} from "@faire/web/common/images/optimizeImages";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import * as React from "react";

export enum ImageTransformationType {
  FillRectangle = "FillRectangle",
  FillSquare = "FillSquare",
  Width = "Width",
  Height = "Height",
  Product = "Product",
  ProductFillVertical = "ProductFillVertical",
}

export type Transformation =
  | {
      type:
        | ImageTransformationType.FillSquare
        | ImageTransformationType.Width
        | ImageTransformationType.Product
        | ImageTransformationType.ProductFillVertical;
      width: number;
    }
  | {
      type: ImageTransformationType.Height;
      height: number;
    }
  | {
      type: ImageTransformationType.FillRectangle;
      width: number;
      height: number;
    };

function loadImage(imageUrl: string, onLoad: () => void) {
  try {
    const window = getWindowOrThrow();
    const img = new window.Image();
    img.onload = onLoad;
    img.src = imageUrl;
    img.onerror = onLoad;
  } catch (_) {
    onLoad();
  }
}

function convertToTinySize(
  transformation: Transformation | undefined
): Transformation | undefined {
  const type = transformation?.type;
  if (!type) {
    return undefined;
  }

  const SMALLER_IMAGE_SIZE = 60;
  switch (type) {
    case ImageTransformationType.FillRectangle:
      return {
        type,
        width: SMALLER_IMAGE_SIZE,
        height: Math.round(
          SMALLER_IMAGE_SIZE * (transformation.height / transformation.width)
        ),
      };

    case ImageTransformationType.Height:
      return { type, height: SMALLER_IMAGE_SIZE };
    case ImageTransformationType.FillSquare:
    case ImageTransformationType.Width:
    case ImageTransformationType.Product:
    case ImageTransformationType.ProductFillVertical:
      return { type, width: SMALLER_IMAGE_SIZE };
    default:
      return exhaustiveSwitchWithDefault(undefined)(type);
  }
}

function doTransformation(
  image: IImage,
  isSSR: boolean,
  options: IOptimizeImageOptions | undefined,
  transformation: Transformation | undefined
) {
  const type = transformation?.type;
  if (!type) {
    return optimizeImageUrl(image, isSSR, options);
  }

  switch (type) {
    case ImageTransformationType.FillRectangle:
      return optimizeImageUrlFillRectangle(
        image,
        isSSR,
        transformation.width,
        transformation.height,
        options
      );
    case ImageTransformationType.FillSquare:
      return optimizeImageUrlFillSquare(
        image,
        isSSR,
        transformation.width,
        options
      );
    case ImageTransformationType.Width:
      return optimizeImageUrlWidth(image, isSSR, transformation.width, options);
    case ImageTransformationType.Height:
      return optimizeImageUrlHeight(
        image,
        isSSR,
        transformation.height,
        options
      );
    case ImageTransformationType.Product:
      return optimizeProductImageUrl(
        image,
        isSSR,
        transformation.width,
        options
      );
    case ImageTransformationType.ProductFillVertical:
      return optimizeProductImageUrlFillVertical(
        image,
        isSSR,
        transformation.width,
        options
      );
    default:
      return exhaustiveSwitchWithDefault(undefined)(type);
  }
}

interface IProgressiveProductImageProps {
  image: IImage;
  isSSR: boolean;
  options?: IOptimizeImageOptions;
  transformation: Transformation;
  className?: string;
  alt: string;
  loading?: HTMLImageElement["loading"];
  onLoad?: () => void;
}

export const ProgressiveProductImage: React.FC<
  IProgressiveProductImageProps
> = ({
  image,
  options,
  transformation,
  isSSR,
  alt,
  loading,
  className,
  onLoad,
}) => {
  const [imageUrl, setImageUrl] = React.useState(() =>
    getInitialImageUrl(image, isSSR, options, transformation)
  );
  const [isInitial, setIsInitial] = React.useState(true);

  React.useEffect(() => {
    const fullSizeUrl = doTransformation(image, isSSR, options, transformation);
    if (fullSizeUrl) {
      loadImage(fullSizeUrl, () => {
        setImageUrl(fullSizeUrl);
        setIsInitial(false);
      });
    } else {
      setIsInitial(false);
    }
  }, [image, isSSR, transformation, options]);

  return (
    <img
      data-faire-image-id={image.token}
      loading={loading}
      src={imageUrl}
      alt={alt}
      className={className}
      data-product-image={isInitial ? "initial" : "full"}
      onLoad={onLoad}
    />
  );
};

function getInitialImageUrl(
  image: IImage,
  isSSR: boolean,
  options: IOptimizeImageOptions | undefined,
  transformation: Transformation
) {
  if (isSSR) {
    /**
     * Use the full-size image for direct loads with SSR.
     * Our client JS bundles are large, and it can take a long time before JS executes.
     * Since we can't render both tiny and full-size images with SSR, let's use the
     * full-size to avoid delaying the start of that request.
     */
    return doTransformation(
      image,
      isSSR,
      {
        ...options,
        // With SSR we don't know the client's DPR, so assume 1.
        dprOverride: 1,
      },
      transformation
    );
  } else {
    return doTransformation(
      image,
      isSSR,
      {
        ...options,
        // It doesn't make sense to have a 2x or 3x tiny image, so use DPR of 1.
        dprOverride: 1,
      },
      convertToTinySize(transformation)
    );
  }
}

const NORMALIZED_IMAGE_WIDTHS = [360, 720, 1080, 1440, 1800];

/**
 * Gets an image and generates a normalized (multiples of 360) srcset for it.
 */
export function getNormalizedImageUrls(
  image: IImage,
  transformation: Transformation,
  options: IOptimizeImageOptions | undefined
): string {
  // we override DPR, isSSR value won't be needed:
  const urls = NORMALIZED_IMAGE_WIDTHS.map((width) => {
    const url = doTransformation(
      image,
      false,
      { ...options, width, dprOverride: 1 },
      { ...transformation, width } as Transformation
    );
    if (url) {
      return `${url} ${width}w`;
    }
    return url;
  }).filter(isNotUndefined);

  return urls.join(", ");
}

export function getDefaultImageUrl(
  defaultSize: number,
  image: IImage,
  transformation: Transformation,
  options: IOptimizeImageOptions | undefined
): string {
  // we override DPR, isSSR value won't be needed:
  const url = doTransformation(
    image,
    false,
    { ...options, width: defaultSize, dprOverride: 1 },
    { ...transformation, width: defaultSize } as Transformation
  );
  if (url) {
    return url;
  }
  // this should not happen (safe default):
  return image.url ?? "";
}
